import React, { useState, useEffect, useRef } from 'react';
import { formatDistanceToNow } from "date-fns";

import { fetchBusinessOrders, updateOrderStatus, cancelOrder } from '../../services/api';
import ConfirmDialog from "../Popup/ConfirmDialog";
import { toast } from "react-toastify";

const OrdersDashboard = ({ clientId, colorData }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const refreshInterval = useRef(null);

  // Define status columns
  const statusColumns = [
    { key: 'pending', title: 'Pending', color: 'warning', icon: 'clock', bgColor: '#fff3cd', textColor: '#856404' },
    { key: 'accepted', title: 'Cooking', color: 'info', icon: 'check-circle', bgColor: '#d1ecf1', textColor: '#0c5460' },
    { key: 'ready', title: 'Ready', color: 'success', icon: 'check', bgColor: '#d4edda', textColor: '#155724' },
    { key: 'delivered', title: 'Delivered', color: 'secondary', icon: 'truck', bgColor: '#e2e3e5', textColor: '#383d41' }
  ];

  // Main load orders function
  const loadOrders = async () => {
    try {
      setLoading(true);
      const data = await fetchBusinessOrders(clientId);

      // Sort orders by newest first within each status
      const sortedData = data.sort((a, b) =>
        new Date(b.created_at || b.date) - new Date(a.created_at || a.date)
      );

      // console.log(sortedData);
      setOrders(sortedData);

      setError(null);
      setLastRefresh(new Date());
    } catch (err) {
      console.error("Error loading orders:", err);
      setError("Failed to sync with the kitchen. Please check connection.");
    } finally {
      setLoading(false);
    }
  };

  const [dialog, setDialog] = useState({
    open: false,
    apiFn: null,
    yesLabel: "",
    onSuccess: null,
  });

  const openDialog = ({ apiFn, yesLabel, onSuccess }) => {
    setDialog({
      open: true,
      apiFn,
      yesLabel,
      onSuccess,
    });
  };

  // Set up auto-refresh
  useEffect(() => {
    if (clientId) {
      // Initial load
      loadOrders();

      // Set up auto-refresh every 30 seconds if enabled
      if (autoRefreshEnabled) {
        refreshInterval.current = setInterval(() => {
          loadOrders();
        }, 30000); // 30 seconds
      }
    }

    // Cleanup interval on unmount
    return () => {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }
    };
  }, [clientId, autoRefreshEnabled]);

  // Toggle auto-refresh
  const toggleAutoRefresh = () => {
    setAutoRefreshEnabled(!autoRefreshEnabled);

    if (!autoRefreshEnabled) {
      // If turning on, start interval
      refreshInterval.current = setInterval(loadOrders, 30000);
    } else {
      // If turning off, clear interval
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }
    }
  };

  // Get orders by status
  const getOrdersByStatus = (status) => {
    return orders.filter(order => order.status === status);
  };

  // Mark order as delivered
  const markAsDelivered = async (orderId) => {
    try {
      const response = await fetch(`/api/orders/${orderId}/delivered`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) throw new Error('Failed to mark as delivered');

      setTimeout(loadOrders, 1000);
    } catch (err) {
      console.error('Error marking order as delivered:', err);
      alert('Failed to update order status');
    }
  };

  if (loading && orders.length === 0) {
    return (
      <div className="container-fluid py-8 text-center">
        <div className="row justify-content-center">
          <div className="col-12">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading live orders...</p>
          </div>
        </div>
      </div>
    );
  }

  // Get maximum number of rows needed
  const maxRows = Math.max(
    getOrdersByStatus('pending').length,
    getOrdersByStatus('accepted').length,
    getOrdersByStatus('ready').length,
    getOrdersByStatus('delivered').length
  );

  return (
    <div className="container-fluid p-4">
      {/* Header */}
      <div className="row mb-4 mt-5"  style={{borderBottom: `5px solid ${colorData?.secondary}`, paddingBottom: "60px"}}>
          <h2 className="poppin text-center">Orders Dashboard</h2>
      </div>

      {/* Real-time Stats Banner */}
      {/* <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body p-3">
              <div className="row g-3">
                <div className="col-md-3 col-6">
                  <div className="text-center">
                    <div className="text-primary display-6 fw-bold mb-2">
                      {orders.length}
                    </div>
                    <div className="text-muted small">
                      <i className="fas fa-clipboard-list me-1"></i>
                      Total Orders
                    </div>
                  </div>
                </div>
                {statusColumns.map((col) => {
                  const count = getOrdersByStatus(col.key).length;
                  return (
                    <div key={col.key} className="col-md-3 col-6">
                      <div className="text-center">
                        <div className={`text-${col.color} display-6 fw-bold mb-2`}>
                          {count}
                        </div>
                        <div className="text-muted small">
                          <i className={`fas fa-${col.icon} me-1`}></i>
                          {col.title}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div> */}

      {/* Error Alert */}
      {error && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-danger alert-dismissible fade show d-flex align-items-center" role="alert">
              <i className="fas fa-exclamation-triangle me-2"></i>
              <div className="flex-grow-1">{error}</div>
              <button
                type="button"
                className="btn-close"
                onClick={() => setError(null)}
              ></button>
            </div>
          </div>
        </div>
      )}

      {/* Kanban Table */}
      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body p-0">
              {orders.length === 0 ? (
                <div className="text-center py-5">
                  <i className="fas fa-clipboard-list fa-3x text-muted opacity-25 mb-3"></i>
                  <h4 className="text-muted mb-3">No orders yet</h4>
                  <p className="text-muted mb-4">Orders will appear here when placed</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-bordered table-hover mb-0" style={{ tableLayout: 'fixed' }}>
                    <thead className="table-light">
                      <tr>
                        {statusColumns.map((col) => (
                          <th
                            key={col.key}
                            className="text-center align-middle "
                            style={{
                              backgroundColor: col.bgColor,
                              color: col.textColor,
                              borderBottom: `3px solid ${col.textColor}`
                            }}
                          >
                            <div className="d-flex flex-column align-items-center">
                              <div className="d-flex align-items-center mb-1">
                                <i className={`fas fa-${col.icon} me-2`}></i>
                                <h5 className="mb-0 fw-bold poppin">{col.title}</h5>
                              </div>
                              <span className="badge bg-white text-dark  mt-2 mb-2">
                                {getOrdersByStatus(col.key).length} order{getOrdersByStatus(col.key).length !== 1 ? 's' : ''}
                              </span>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from({ length: maxRows }).map((_, rowIndex) => (
                        <tr key={rowIndex} style={{ height: '120px' }}>
                          {statusColumns.map((col) => {
                            const columnOrders = getOrdersByStatus(col.key);
                            const order = columnOrders[rowIndex];

                            return (
                              <td
                                key={col.key}
                                className="align-top p-2"
                                style={{
                                  backgroundColor: col.bgColor + '20', // 20% opacity
                                  verticalAlign: 'top'
                                }}
                              >
                                {order ? (
                                  <div
                                    className="card h-100 border-0"
                                    style={{ backgroundColor: 'white', borderRadius: "0" }}
                                  >
                                    <div className="card-body p-3">
                                      {/* Order Header */}
                                      <div className="d-flex justify-content-between align-items-start mb-2">
                                        <span className="badge bg-secondary">
                                          #{order.id}
                                        </span>
                                        <small className="text-muted">
                                          {formatDistanceToNow(new Date(order.created_at), { addSuffix: false }).replace(" ago", "")}
                                        </small>
                                      </div>

                                      {/* Customer Info */}
                                      <h6 className="card-title mb-3">
                                        <i className="fas fa-user text-primary me-1 "></i>
                                        <span className='poppin'>{order.customer_name}</span>
                                      </h6>

                                      {/* Items */}
                                      <div className="mb-2">
                                        <small className="text-muted d-block">
                                          {order.items.slice(0, 2).map((item, idx) => (
                                            <div key={idx} className="d-flex justify-content-between mb-3">
                                              <span className='poppin'>
                                                <span className="badge bg-primary me-1">{item.qty}x</span>
                                                {item.name}
                                              </span>
                                            </div>
                                          ))}
                                          {order.items.length > 2 && (
                                            <small className="text-muted poppin">
                                              +{order.items.length - 2} more
                                            </small>
                                          )}
                                        </small>
                                      </div>

                                      {/* Total */}
                                      <div className="d-flex justify-content-between align-items-center mt-2">
                                        <small className="fw-bold text-black fs-4 poppin">
                                          £{parseFloat(order.total_amount).toFixed(2)}
                                        </small>

                                        {/* Actions */}
                                        <div className="d-flex gap-1">
                                          {order.status === 'pending' && (
                                            <>
                                              <button
                                                onClick={() =>
                                                  openDialog({
                                                    yesLabel: "Accept It",
                                                    apiFn: () => updateOrderStatus(order.id, 'accepted', ''),
                                                     onSuccess: () => {
                                                      toast.success("Order Accpted");
                                                      loadOrders();
                                                    }
                                                  })
                                                }
                                                className="btn btn-sm btn-success"
                                                title="Mark as Ready"
                                              >
                                                <i className="fas fa-check"></i>
                                              </button>
                                              <button
                                                onClick={() =>
                                                  openDialog({
                                                    yesLabel: "Cancel",
                                                    apiFn: () => cancelOrder(order.id, "No reason"),
                                                    onSuccess: () => {
                                                      toast.success("Order Cancelled");
                                                      loadOrders();
                                                    }
                                                  })
                                                }
                                                className="btn btn-sm btn-outline-danger"
                                                title="Cancel Order"
                                              >
                                                <i className="fas fa-times"></i>
                                              </button>
                                            </>
                                          )}
                                          {order.status === 'accepted' && (
                                            <button
                                              onClick={() =>
                                                  openDialog({
                                                    yesLabel: "Set Ready",
                                                    apiFn: () => updateOrderStatus(order.id, 'ready', ''),
                                                     onSuccess: () => {
                                                      toast.success("Order Ready");
                                                      loadOrders();
                                                    }
                                                  })
                                                }
                                              className="btn-glass-success"
                                              title="Order is ready"
                                            >
                                              <i className="fas fa-truck"></i>
                                            </button>
                                          )}
                                          {order.status === 'ready' && (
                                            <button
                                              onClick={() => markAsDelivered(order.id)}
                                              className="btn btn-sm btn-info"
                                              title="Mark as Delivered"
                                            >
                                              <i className="fas fa-truck"></i>
                                            </button>
                                          )}
                                          {(order.status === 'accepted' || order.status === 'delivered') && (
                                            <button
                                              className="btn btn-sm btn-outline-secondary"
                                              onClick={() => {
                                                // View order details
                                                toast.info(`Viewing order #${order.id}`);
                                              }}
                                              title="View Details"
                                            >
                                              <i className="fas fa-eye"></i>
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div
                                    className="h-100 d-flex align-items-center justify-content-center"
                                    style={{ minHeight: '100px' }}
                                  >
                                    <span className="text-muted">-</span>
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Table Footer */}
            <div className="card-footer bg-light py-2">
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">
                  Total: {orders.length} orders across {statusColumns.length} statuses
                </small>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={loadOrders}
                  >
                    <i className="fas fa-sync-alt me-1"></i>
                    Refresh Orders
                  </button>
                  <button
                    className={`btn btn-sm ${autoRefreshEnabled ? 'btn-warning' : 'btn-outline-success'}`}
                    onClick={toggleAutoRefresh}
                  >
                    <i className={`fas fa-${autoRefreshEnabled ? 'pause' : 'play'} me-1`}></i>
                    {autoRefreshEnabled ? 'Disable Auto-Refresh' : 'Enable Auto-Refresh'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Single shared dialog */}
      <ConfirmDialog
        open={dialog.open}
        yesLabel={dialog.yesLabel}
        apiFn={dialog.apiFn}
        onSuccess={dialog.onSuccess}
        onClose={() => setDialog((d) => ({ ...d, open: false }))}
      />
    </div>
  );
};

export default OrdersDashboard;